import { supabase } from '../lib/supabase';
import type { Media } from '../types/media';
import { logger } from '../lib/logger';

export async function getMedia(userId: string): Promise<Media[]> {
  const { data, error } = await supabase
    .from('media')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function searchMedia(userId: string, query: string): Promise<Media[]> {
  const { data, error } = await supabase
    .from('media')
    .select('*')
    .eq('user_id', userId)
    .or(`filename.ilike.%${query}%,alt_text.ilike.%${query}%`)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function uploadMedia(
  userId: string,
  file: File,
  altText?: string
): Promise<Media> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('media')
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage.from('media').getPublicUrl(storagePath);

  let width: number | undefined;
  let height: number | undefined;

  if (file.type.startsWith('image/')) {
    try {
      const dimensions = await getImageDimensions(file);
      width = dimensions.width;
      height = dimensions.height;
    } catch {
      // Ignore dimension errors
    }
  }

  const { data, error } = await supabase
    .from('media')
    .insert({
      user_id: userId,
      filename: file.name,
      storage_path: storagePath,
      url: urlData.publicUrl,
      mime_type: file.type,
      size: file.size,
      width,
      height,
      alt_text: altText,
    })
    .select()
    .single();

  if (error) {
    await supabase.storage.from('media').remove([storagePath]);
    throw error;
  }

  return data;
}

export async function updateMediaAltText(
  mediaId: string,
  userId: string,
  altText: string
): Promise<Media> {
  const { data, error } = await supabase
    .from('media')
    .update({ alt_text: altText })
    .eq('id', mediaId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteMedia(mediaId: string, userId: string): Promise<void> {
  const { data: media, error: fetchError } = await supabase
    .from('media')
    .select('storage_path')
    .eq('id', mediaId)
    .eq('user_id', userId)
    .maybeSingle();

  if (fetchError) throw fetchError;
  if (!media) throw new Error('Media not found');

  const { error: storageError } = await supabase.storage
    .from('media')
    .remove([media.storage_path]);

  if (storageError) {
    logger.error('Storage delete error:', storageError);
  }

  const { error: dbError } = await supabase
    .from('media')
    .delete()
    .eq('id', mediaId)
    .eq('user_id', userId);

  if (dbError) throw dbError;
}

export async function deleteMultipleMedia(mediaIds: string[], userId: string): Promise<void> {
  const { data: mediaList, error: fetchError } = await supabase
    .from('media')
    .select('id, storage_path')
    .in('id', mediaIds)
    .eq('user_id', userId);

  if (fetchError) throw fetchError;
  if (!mediaList || mediaList.length === 0) return;

  const storagePaths = mediaList.map((m) => m.storage_path);

  const { error: storageError } = await supabase.storage
    .from('media')
    .remove(storagePaths);

  if (storageError) {
    logger.error('Storage delete error:', storageError);
  }

  const { error: dbError } = await supabase
    .from('media')
    .delete()
    .in('id', mediaIds)
    .eq('user_id', userId);

  if (dbError) throw dbError;
}

function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
